import Container from "@material-ui/core/Container";
import SearchIcon from "@material-ui/icons/Search";
import React, { FC, useEffect } from "react";
import { FormControl, InputAdornment, InputBase, Button, ButtonProps, Divider } from "@material-ui/core";
import useStyles from "./styles";
import useCustomGTMId from "Hooks/useCustomGTMId";
import { useLocation } from "Hooks/useRouter";
import InternalRoutes from "Shared/Constants/InternalRoutes";
import { queryToProps } from "Api";

export type DPSearchBarProps = {
  disabled?: boolean;
  submitSearch: (search: string) => void;
  placeholder?: string;
  buttonProps?: ButtonProps;
  displayDivider?: boolean;
  rootComponentStyles?: string;
  inputStyles?: string;
};

const DPSearchBar: FC<DPSearchBarProps> = ({
  disabled,
  submitSearch,
  placeholder,
  buttonProps,
  displayDivider,
  rootComponentStyles,
  inputStyles,
}) => {
  const classes = useStyles();

  const { pathname, ...location } = useLocation();
  const { search } = queryToProps(location.search);

  const [searchInput, setSearchInput] = React.useState(search);

  useEffect(() => {
    setSearchInput(search || "");
  }, [search]);

  useEffect(() => {
    if (
      searchInput &&
      pathname !== InternalRoutes.users &&
      pathname !== InternalRoutes.presettlementExclusions
    ) {
      setSearchInput("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const searchInputRef = useCustomGTMId<HTMLDivElement>("search_input");
  const searchButtonRef = useCustomGTMId<HTMLButtonElement>("search_button");
  const searchIconRef = useCustomGTMId<SVGSVGElement>("search_icon");

  const onChangeHandler = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(evt.target.value);
  };

  const onKeyPressHandler = (evt: React.KeyboardEvent<HTMLDivElement>) => {
    if (evt.key === "Enter") {
      evt.preventDefault();
      submitSearch(searchInput);
    }
  };

  const onSubmitSearchClickHandler = () => {
    submitSearch(searchInput);
  };

  return (
    <Container className={rootComponentStyles || classes.root}>
      <FormControl color="primary" className={classes.searchWrapper}>
        <InputBase
          autoFocus
          id="user-search"
          classes={{
            input: inputStyles || classes.searchInput,
            focused: classes.searchFocused,
          }}
          aria-describedby="search-help-text"
          value={searchInput}
          type="search"
          autoComplete="off"
          placeholder={placeholder}
          onChange={onChangeHandler}
          onKeyPress={onKeyPressHandler}
          endAdornment={
            <InputAdornment
              position="end"
              ref={searchInputRef}
              className={classes.searchButtonWrapper}
            >
              {displayDivider && (
                <Divider className={classes.divider} orientation="vertical" />
              )}
              <Button
                aria-label="Search"
                variant="contained"
                disableFocusRipple
                disabled={disabled}
                ref={searchButtonRef}
                color="primary"
                className={classes.searchButton}
                onClick={onSubmitSearchClickHandler}
                {...buttonProps}
              >
                <SearchIcon ref={searchIconRef} fontSize={"small"} />
              </Button>
            </InputAdornment>
          }
        />
      </FormControl>
    </Container>
  );
};

export default DPSearchBar;
